import React, { useState, memo, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const InventionNo8Viewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Invention No. 8 BWV 779
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV779/bach-invention-08/bach-invention-08-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Invention No. 8 BWV 779.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-5181">
      <Navbar data-id="element-5182" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-5183">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-5184">
          <img src="https://images.unsplash.com/photo-1520523839897-bd0b52f945a0?w=400&h=300&fit=crop" alt="Invention No. 8" className="w-full h-64 md:h-80 object-cover" data-id="element-5185" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-5186" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-5187">
            <div className="max-w-7xl mx-auto" data-id="element-5188">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-5189">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-5190" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-5191">
                Invention No. 8 in F major
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-5192">BWV 779</p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-5193">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-5194">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-5195">
          <div className="flex items-center space-x-2" data-id="element-5196">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-5197">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-5198" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5199">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-5200">
                  <ZoomInIcon className="h-5 w-5" data-id="element-5201" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-5202">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-5203">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-5204" />
              <span className="hidden sm:inline" data-id="element-5205">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-5206">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-5207" />
              <span className="hidden sm:inline" data-id="element-5208">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-5209">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-5210">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-5211">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Invention No. 8 Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-5212" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-5213">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5214">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-5215">
            <div data-id="element-5216">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5217">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-5218">
                <div data-id="element-5219">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5220">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5221">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-5222">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5223">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5224">
                    c. 1720-1723
                  </dd>
                </div>
                <div data-id="element-5225">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5226">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5227">
                    Two-Part Invention
                  </dd>
                </div>
                <div data-id="element-5228">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5229">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5230">
                    F major
                  </dd>
                </div>
                <div data-id="element-5231">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5232">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5233">
                    BWV 779
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-5234">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5235">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5236">
                The eighth of Bach's fifteen Two-Part Inventions, this piece in
                F major stands out for its bright, cheerful character and
                elegant simplicity. Written as pedagogical works for keyboard
                students, the Inventions teach the art of two-voice counterpoint
                while developing musical ideas through imitation and
                development. This invention features a graceful, flowing subject
                that moves in smooth, stepwise motion, creating a sense of ease
                and natural musicality. The major key gives the work a sunny,
                optimistic quality that makes it particularly appealing to both
                performers and listeners. The piece demonstrates Bach's gift for
                creating sophisticated musical architecture within an accessible
                framework, with the two voices engaging in a continuous dialogue
                that feels both conversational and formally balanced. Technical
                challenges include maintaining independence between the voices
                while ensuring smooth legato playing, managing the hand
                crossings that occur when the melodic lines intersect, and
                bringing out the subtle harmonic progressions that give the
                piece its expressive depth. This invention serves as both an
                essential pedagogical study in baroque keyboard technique and a
                charming miniature that showcases Bach's ability to create
                memorable musical statements within the constraints of a
                teaching piece, demonstrating that educational music can be both
                instructive and artistically satisfying.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-5237" />
    </div>;
};